import React from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { BarChart3, Shield, TrendingUp, Calculator, ArrowRight, Activity, LineChart, CheckCircle, XCircle } from 'lucide-react';

const Home = () => {
  const { user } = useAuth();

  const features = [
    { icon: Shield, title: 'Risk Assessment', desc: 'Understand your risk appetite through a quick personalized questionnaire tailored to your profile.', color: 'text-green-600', bg: 'bg-green-50' },
    { icon: TrendingUp, title: 'Smart Portfolio', desc: 'Get a diversified portfolio across top Indian stocks, debt instruments and gold — built for your risk level.', color: 'text-blue-600', bg: 'bg-blue-50' },
    { icon: Calculator, title: 'SIP Planner', desc: 'Plan your monthly investments with smart projections and an emergency buffer built in.', color: 'text-purple-600', bg: 'bg-purple-50' },
    { icon: BarChart3, title: 'Corpus Projection', desc: 'Visualize your wealth growth year-by-year and track progress towards your financial goals.', color: 'text-orange-600', bg: 'bg-orange-50' },
    { icon: Activity, title: 'Live Market Data', desc: 'Stay updated with real-time stock prices, daily changes and volume for your portfolio stocks.', color: 'text-red-600', bg: 'bg-red-50' },
    { icon: LineChart, title: 'Backtesting', desc: 'See how your portfolio would have performed historically compared to the market benchmark.', color: 'text-teal-600', bg: 'bg-teal-50' },
  ];

  const uspComparison = [
    { feature: 'Risk Profiling', us: 'Multi-factor personalized analysis', others: 'Simple questionnaire only' },
    { feature: 'Portfolio Stocks', us: 'Top 10 multi-sector Indian stocks', others: '3-5 stocks, single sector' },
    { feature: 'Asset Classes', us: 'Equity + Debt + Gold', others: 'Equity only' },
    { feature: 'Intelligence', us: 'AI-driven smart recommendations', others: 'Rule-based suggestions' },
    { feature: 'SIP Planning', us: 'Income-aware with multiple scenarios', others: 'Fixed amount calculator' },
    { feature: 'Rebalancing', us: 'Automatic drift alerts', others: 'Manual / none' },
    { feature: 'Backtesting', us: 'Historical performance comparison', others: 'Not available' },
    { feature: 'Reports', us: 'Comprehensive PDF reports', others: 'Basic summary' },
  ];

  return (
    <div className="min-h-[80vh]">
      {/* Hero */}
      <section className="text-center py-16 px-4 bg-gradient-to-b from-white to-primary-50">
        <div className="max-w-3xl mx-auto">
          <div className="flex justify-center mb-4">
            <div className="bg-primary-100 p-4 rounded-2xl">
              <BarChart3 className="h-12 w-12 text-primary-600" />
            </div>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            AI-Powered <span className="text-primary-600">Robo-Advisor</span>
          </h1>
          <p className="text-lg text-gray-600 mb-8 max-w-2xl mx-auto">
            Smart investing made simple. Get personalized portfolio recommendations 
            designed for your financial goals and risk comfort.
          </p>
          <div className="flex justify-center gap-4">
            {user ? (
              <Link to="/dashboard" className="btn-primary flex items-center gap-2">
                Go to Dashboard <ArrowRight className="h-4 w-4" />
              </Link>
            ) : (
              <>
                <Link to="/register" className="btn-primary flex items-center gap-2">
                  Get Started Free <ArrowRight className="h-4 w-4" />
                </Link>
                <Link to="/login" className="btn-secondary">
                  Login
                </Link>
              </>
            )}
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="max-w-6xl mx-auto px-4 py-12">
        <h2 className="text-2xl font-bold text-center text-gray-900 mb-10">Key Features</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {features.map((f, idx) => {
            const Icon = f.icon;
            return (
              <div key={idx} className="card text-center hover:shadow-lg transition-shadow">
                <div className={`inline-flex p-3 rounded-xl ${f.bg} mb-4`}>
                  <Icon className={`h-7 w-7 ${f.color}`} />
                </div>
                <h3 className="font-semibold text-gray-800 mb-2">{f.title}</h3>
                <p className="text-sm text-gray-500">{f.desc}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* How It Works */}
      <section className="bg-gray-900 py-14 px-4">
        <div className="max-w-5xl mx-auto text-center">
          <h2 className="text-2xl font-bold text-white mb-2">How It Works</h2>
          <p className="text-gray-400 mb-10 text-sm">Your journey to smarter investing in 3 simple steps</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { step: '1', title: 'Take the Risk Quiz', desc: 'Answer a few quick questions so we can understand your financial profile and comfort with risk.', color: 'bg-blue-500' },
              { step: '2', title: 'Get Your Portfolio', desc: 'We build a personalized portfolio of top Indian stocks, debt and gold based on your profile.', color: 'bg-green-500' },
              { step: '3', title: 'Plan & Track', desc: 'Set up your SIP, track growth projections, get rebalancing alerts and download PDF reports.', color: 'bg-orange-500' },
            ].map((s, idx) => (
              <div key={idx} className="flex flex-col items-center">
                <div className={`w-14 h-14 ${s.color} text-white rounded-full flex items-center justify-center text-2xl font-bold mb-4`}>
                  {s.step}
                </div>
                <h3 className="text-white font-semibold mb-2">{s.title}</h3>
                <p className="text-gray-400 text-sm max-w-[250px]">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* USP Comparison Table */}
      <section className="max-w-4xl mx-auto px-4 py-14">
        <h2 className="text-2xl font-bold text-center text-gray-900 mb-2">Why Choose Us?</h2>
        <p className="text-center text-gray-500 mb-8 text-sm">How our Robo-Advisor compares to typical investment platforms</p>
        <div className="card overflow-hidden p-0">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-b">
                <th className="text-left py-3 px-4 font-semibold text-gray-700">Feature</th>
                <th className="text-left py-3 px-4 font-semibold text-primary-700">Our Platform</th>
                <th className="text-left py-3 px-4 font-semibold text-gray-500">Others</th>
              </tr>
            </thead>
            <tbody>
              {uspComparison.map((row, idx) => (
                <tr key={idx} className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="py-3 px-4 font-medium text-gray-700">{row.feature}</td>
                  <td className="py-3 px-4 text-primary-700 flex items-start gap-1.5">
                    <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                    {row.us}
                  </td>
                  <td className="py-3 px-4 text-gray-500">
                    <span className="flex items-start gap-1.5">
                      <XCircle className="h-4 w-4 text-red-400 mt-0.5 flex-shrink-0" />
                      {row.others}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-primary-50 py-12 px-4">
        <div className="max-w-2xl mx-auto text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-3">Ready to Start Investing Smarter?</h2>
          <p className="text-gray-600 mb-6">Join thousands of investors who trust Algodhan for their financial planning.</p>
          {!user && (
            <Link to="/register" className="btn-primary inline-flex items-center gap-2">
              Create Free Account <ArrowRight className="h-4 w-4" />
            </Link>
          )}
        </div>
      </section>
    </div>
  );
};

export default Home;
